import React from 'react';
import Card, { CardProps as MuiCardProps } from '@material-ui/core/Card';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { makeTransformStyles, makeTransitionStyles } from '../../styles';
import CardActionArea, {
  CardActionAreaProps as MuiCardActionAreaProps,
} from '@material-ui/core/CardActionArea';
import clsx from 'clsx';
import CardContent from '@material-ui/core/CardContent';
import AppAvatar, { AppAvatarProps } from '../../Avatar/AppAvatar';
import SkeletonTypography, {
  SkeletonTypographyProps,
} from '../../Typography/SkeletonTypography';

type ClassKey = 'root' | 'area' | 'content' | 'avatar' | 'img' | 'role';

export type Props = {
  loaded?: boolean,
  firstName?: string | null,
  lastName?: string | null,
  role?: string | null,
  imageUrl?: string | null,
  remark?: string | null,
  classes?: {
    [key: ClassKey]: string,
  },
  CardProps?: MuiCardProps,
  CardActionAreaProps?: MuiCardActionAreaProps,
  AvatarComponent?: React.ComponentType<AppAvatarProps>,
  avatarRenderer?: (avatar: React.ReactNode) => React.ReactNode,
  TypoComponent?: React.ComponentType<SkeletonTypographyProps>,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: 80,
    height: '100%',
    color: theme.palette.common.white,
    [theme.breakpoints.up('sm')]: {
      width: 100,
    },
    '&:hover img': makeTransformStyles('scale(1.1)'),
  },
  area: {
    height: '100%',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  content: {
    padding: theme.spacing(2, 0),
  },
  avatar: {
    width: 80,
    height: 80,
    [theme.breakpoints.up('sm')]: {
      width: 100,
      height: 100,
    },
  },
  img: {
    ...makeTransitionStyles('all .3s ease'),
    width: '100%',
    height: 'auto',
  },
  role: {
    paddingTop: theme.spacing(0.5),
    height: 22,
  },
}));

const PersonInfo = ({
  loaded,
  firstName,
  lastName,
  role,
  imageUrl,
  classes: classesProps,
  CardProps,
  CardActionAreaProps,
  AvatarComponent = AppAvatar,
  avatarRenderer,
  TypoComponent = SkeletonTypography,
}: Props) => {
  const classes = useStyles({ classes: classesProps });

  const fullName = [lastName, firstName].filter(Boolean).join(' ');

  const avatar = (
    <AvatarComponent
      loaded={loaded}
      src={imageUrl}
      className={classes.avatar}
      imgProps={{ className: classes.img }}
    />
  );

  return (
    <Card
      elevation={0}
      {...CardProps}
      className={clsx(classes.root, CardProps?.className)}
    >
      <CardActionArea
        disableRipple
        disableTouchRipple
        {...CardActionAreaProps}
        className={clsx(classes.area, CardActionAreaProps?.className)}
        component="div"
      >
        {avatarRenderer?.(avatar) || avatar}
        <CardContent className={classes.content}>
          <TypoComponent
            color="textPrimary"
            className="line-truncate-2"
            loaded={loaded}
            title={fullName}
            fontSize={12}
            align="center"
            width="60%"
          >
            {fullName}
          </TypoComponent>
          <TypoComponent
            color="textSecondary"
            className={clsx(classes.role, 'line-truncate-2')}
            loaded={loaded}
            fontSize={12}
            align="center"
            width="60%"
          >
            {role || ' '}
          </TypoComponent>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default PersonInfo;
